import {
  ApplicationConfig,
  APP_INITIALIZER,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withViewTransitions, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { appRoutes } from './app.routes';
import { KeycloakService } from 'keycloak-angular';
import {
  API_BASE_URL,
  initializeKeycloak,
  KEYCLOAK_CONFIG,
  authTokenInterceptor,
  errorInterceptor,
  changeDetectionInterceptor,
} from '@angular-workspace/core';
import {
  USER_API_PATH,
  ROLES_API_PATH,
  ROLE_UPGRADE_API_PATH,
  AUTH_API_PATH,
  CAT_API_PATH,
  AUDITING_API_PATH,
  ACL_APD_API_PATH,
  FILE_BASE_URL,
  FILE_API_PATH,
  LEADERSHIP_API_PATH,
  SANDBOX_BASE_URL,
  USER_INTERACTION_API_PATH,
  COMMUNITY_BASE_URL,
  CHALLENGE_BASE_URL,
  RS_BASE_URL,
  OGC_TILES_BASE_URL,
} from '@angular-workspace/services';
import { environment } from '../environments/environment';
import { AuthListenerService } from './services/auth-listener.service';
import { GoogleAnalyticsService } from './services/google-analytics.service';

function keycloakInitializer(keycloak: KeycloakService) {
  return initializeKeycloak(keycloak, environment.keycloak);
}

function authListenerInitializer(authListener: AuthListenerService) {
  return () => {
    setTimeout(() => authListener.initialize(), 0);
    return Promise.resolve();
  };
}

function googleAnalyticsInitializer(analytics: GoogleAnalyticsService) {
  return () => {
    setTimeout(() => analytics.initialize(), 0);
    return Promise.resolve();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimationsAsync(),
    provideRouter(
      appRoutes,
      withViewTransitions(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      })
    ),
    provideHttpClient(
      withInterceptors([authTokenInterceptor, errorInterceptor, changeDetectionInterceptor])
    ),
    { provide: API_BASE_URL, useValue: environment.basePathUrl },
    { provide: KEYCLOAK_CONFIG, useValue: environment.keycloak },
    { provide: USER_API_PATH, useValue: environment.userApiPath },
    { provide: ROLES_API_PATH, useValue: environment.rolesApiPath },
    { provide: ROLE_UPGRADE_API_PATH, useValue: environment.roleUpgradeApiPath },
    { provide: AUTH_API_PATH, useValue: environment.authApiPath },
    { provide: CAT_API_PATH, useValue: environment.catApiPath },
    { provide: AUDITING_API_PATH, useValue: environment.auditingApiPath },
    { provide: ACL_APD_API_PATH, useValue: environment.aclapdPath },
    { provide: FILE_BASE_URL, useValue: environment.baseFilePathUrl },
    { provide: FILE_API_PATH, useValue: '' },
    { provide: RS_BASE_URL, useValue: environment.baseRsPathUrl },
    { provide: OGC_TILES_BASE_URL, useValue: environment.api_ogc_server_domain },
    { provide: LEADERSHIP_API_PATH, useValue: environment.leadershipApiPath },
    { provide: SANDBOX_BASE_URL, useValue: environment.baseSandboxPathUrlCpu },
    { provide: USER_INTERACTION_API_PATH, useValue: environment.userInteractionApiPath },
    { provide: COMMUNITY_BASE_URL, useValue: environment.baseCommunityPathUrl },
    { provide: CHALLENGE_BASE_URL, useValue: environment.baseChallengePathUrl },
    KeycloakService,
    AuthListenerService,
    {
      provide: APP_INITIALIZER,
      useFactory: keycloakInitializer,
      multi: true,
      deps: [KeycloakService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: authListenerInitializer,
      multi: true,
      deps: [AuthListenerService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: googleAnalyticsInitializer,
      multi: true,
      deps: [GoogleAnalyticsService],
    },
  ],
};
